// Tuning for the 3D AI mascot. Clip, node, material and shape-key names match the GLB built by
// tools/mascot (see README.md there); renaming one there means renaming it here.
import type { MascotQuality, MascotState } from "./mascot.types";

export const MASCOT_CLIPS: Record<MascotState, string> = {
  idle: "Idle",
  walking: "Walking",
  listening: "Listening",
  thinking: "Thinking",
  talking: "Talking",
  done: "Done",
  sitting: "Sitting",
  waving: "Waving",
  pulling: "Pulling",
  /** root's right hand on the handle: a board on the viewer's left (no model flip, D-063). */
  pullingRight: "PullingRight",
};

/** Crossfade seconds; per transition where the default reads too slow or too quick. */
export const CROSSFADE_DEFAULT = 0.3;
export const CROSSFADE: Partial<Record<`${MascotState}>${MascotState}`, number>> = {
  "idle>walking": 0.2,
  "walking>idle": 0.35,
  "idle>listening": 0.25,
  "listening>thinking": 0.35,
  "thinking>talking": 0.25,
  "talking>done": 0.25,
  "done>idle": 0.45,
  "sitting>idle": 0.5,
  "idle>sitting": 0.5,
  "idle>waving": 0.3,
  "waving>idle": 0.45,
  "waving>thinking": 0.35,
  "walking>pulling": 0.3,
  "pulling>talking": 0.35,
  "walking>pullingRight": 0.3,
  "pullingRight>talking": 0.35,
  "talking>walking": 0.3,
  "walking>waving": 0.35,
};

export function crossfadeFor(from: MascotState | null, to: MascotState): number {
  if (!from) return 0;
  return CROSSFADE[`${from}>${to}`] ?? CROSSFADE_DEFAULT;
}

/** Reduced motion: each state holds one representative frame of its clip (seconds). */
export const REDUCED_MOTION_POSE: Record<MascotState, number> = {
  idle: 0,
  walking: 0,
  listening: 0.75,
  thinking: 1.0,
  talking: 0.6,
  done: 0.75,
  sitting: 0.8,
  waving: 0.125,
  pulling: 0,
  pullingRight: 0,
};

/** Face shape keys (morph targets on Mascot_Face). Left/Right are the character's own sides. */
export const SHAPE = {
  blinkLeft: "Blink_Left",
  blinkRight: "Blink_Right",
  blink: "Blink_Both",
  neutral: "Mouth_Neutral",
  smile: "Mouth_Smile",
  open: "Mouth_Open",
  smallOpen: "Mouth_SmallOpen",
  wide: "Mouth_Wide",
  frown: "Mouth_Frown",
  happy: "Eye_Happy",
  thinking: "Eye_Thinking",
  listening: "Eye_Listening",
  lookLeft: "Eye_LookLeft",
  lookRight: "Eye_LookRight",
  lookUp: "Eye_LookUp",
  lookDown: "Eye_LookDown",
} as const;

/**
 * Self-illumination per material name. The GLB has no emissive (glTF cannot tie it to vertex colour);
 * MascotModel multiplies this by the vertex colour so glow rings, eyes and accents light up in their
 * own gradient. Glow pulses while listening.
 */
export const EMISSIVE: Record<string, number> = { Accent: 0.12, Glow: 1.35, Eye: 0.2 };
export const LISTEN_GLOW_PULSE = 0.4;

/** Blender units -> scene units: the mascot (feet to antenna tips) is 1 unit tall. */
export const MODEL_HEIGHT = 3.97;
export const MODEL_SCALE = 1 / MODEL_HEIGHT;
/** Half extents the camera must keep in frame (scene units, gestures included), and the aim point. */
export const FRAME = { halfWidth: 0.42, halfHeight: 0.54, centerY: 0.5 } as const;
export const CAMERA = { fov: 26, direction: [0.32, 0.14, 1] as const } as const;
/** Head-only frame (chin to antenna tips, ear to ear), nearly front-on for small icons. */
export const HEAD_FRAME = {
  halfWidth: 0.37,
  halfHeight: 0.3,
  centerY: 0.705,
  fov: 22,
  direction: [0.14, 0.06, 1] as const,
} as const;

/** Strolling root (Home and Orders header): a wide strip, framed on root's height, nearly front-on. */
export const STROLL_FRAME = {
  halfWidth: 0.1,
  halfHeight: 0.56,
  centerY: 0.5,
  fov: 18,
  direction: [0, 0.12, 1] as const,
} as const;
export const STROLL = {
  /** Walk cadence while strolling (feet stay in sync: speed follows it). */
  timeScale: 1.6,
  /** Yaw while walking (radians, 3/4 toward the walking direction) and turn rate (radians/s). */
  heading: 0.95,
  turnRate: 4,
  /** Keep this far (scene units) from the strip's ends. */
  edge: 0.42,
  /** Pause length range (s) and the chance a pause is a thumbs-up (Done) instead of Idle. */
  pause: [2.2, 4.5] as const,
  doneChance: 0.25,
} as const;

/** Sitting root (Marketing → Promotions, before New coupon): a small square, framed on the seated
 * height, turned a little toward the viewer. */
export const SIT_FRAME = {
  halfWidth: 0.4,
  halfHeight: 0.46,
  centerY: 0.43,
  fov: 20,
  direction: [0, 0.15, 1] as const,
} as const;
export const SIT_HEADING = 0.35;

/**
 * The log-in stage (D-063): root walks and pulls feature boards across an orthographic stage, so one
 * scene unit is exactly root's height in CSS px. GRIP is where the Pulling clip's left fist sits,
 * in root heights from its feet (measured from the rig: 1.30, 1.55 Blender units at 3.97 tall).
 */
export const LOGIN = {
  grip: { x: 0.33, y: 0.39 },
  /** Walking cadence and pace on the stage (feet stay planted at this pace). */
  walkTimeScale: 2,
  /** Pulling: side-steps, a little slower than walking, the board in hand. */
  pullTimeScale: 1.5,
  pullSpeed: 0.45,
  /** Heading while walking (radians) and when presenting a board. */
  walkHeading: 0.95,
  presentHeading: 0.35,
  turnRate: 6,
} as const;

/** Walking covers 0.6 Blender units per second at timeScale 1 (stride 0.3 per half cycle). */
export const WALK_SPEED = 0.6 * MODEL_SCALE;
export const ENTRANCE = { distance: 0.32, timeScale: 1.3, heading: 0.75, turnBack: 0.45 } as const;

export const BLINK = { minGap: 2.2, maxGap: 6.0, duration: 0.17, doubleChance: 0.18 } as const;

export interface QualityTier {
  dpr: [number, number];
  antialias: boolean;
  /** Image-based reflections (RoomEnvironment PMREM, generated once; no download). */
  environment: boolean;
  /** Keep MeshPhysicalMaterial (clearcoat); off swaps in MeshStandardMaterial. */
  physical: boolean;
}

export const QUALITY: Record<Exclude<MascotQuality, "auto">, QualityTier> = {
  "desktop-high": { dpr: [1, 2], antialias: true, environment: true, physical: true },
  "desktop-normal": { dpr: [1, 1.5], antialias: true, environment: true, physical: true },
  mobile: { dpr: [1, 1.25], antialias: false, environment: false, physical: false },
};
